import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import styles from './Slider.module.scss';
import { ContentCardProps, UI_ContentCard } from 'ui/components/2-molecules/Card/ContentCard/ContentCard';
import { useRef, useEffect, useState } from 'react';
import { UI_Icon } from 'ui/components/1-atoms/Media/Icon/Icon';
import ChevronLeft from 'ui/icons/icon-chevron-left.svg';
import ChevronRight from 'ui/icons/icon-chevron-right.svg';
import classNames from 'classnames';

export interface SliderProps {
	className?: string;
	slides: ContentCardProps[];
	prevSlideTitle?: string;
	nextSlideTitle?: string;
}

export const UI_Slider = ({ slides, prevSlideTitle, nextSlideTitle }: SliderProps) => {
	const prevRef = useRef<HTMLButtonElement>(null);
	const nextRef = useRef<HTMLButtonElement>(null);
	const swiperRef = useRef(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);
	const [isSlimContent, setIsSlimContent] = useState<null | boolean>(null);

	useEffect(() => {
		if (rootRef.current && (rootRef.current.closest('.u-slim-content-width') || rootRef.current.closest('.u-narrow-content-width'))) {
			setIsSlimContent(true);
		} else {
			setIsSlimContent(false);
		}
	}, []);

	const handleSwiperInit = (swiper) => {
		swiperRef.current = swiper;
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);

		// Initialize navigation after swiper is ready
		setTimeout(() => {
			if (swiper && swiper.params && prevRef.current && nextRef.current) {
				// Ensure navigation params object exists
				if (!swiper.params.navigation) {
					swiper.params.navigation = {};
				}

				swiper.params.navigation.prevEl = prevRef.current;
				swiper.params.navigation.nextEl = nextRef.current;

				// Initialize navigation if it exists
				if (swiper.navigation && typeof swiper.navigation.init === 'function') {
					swiper.navigation.init();
					swiper.navigation.update();
				}
			}
		}, 0);
	};

	const handleSlideChange = (swiper) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	const breakpoints = isSlimContent
		? {
				320: { slidesPerView: 1.2, spaceBetween: 20 },
				768: { slidesPerView: 1.2, spaceBetween: 20 },
				1024: { slidesPerView: 2, spaceBetween: 40 },
			}
		: {
				320: { slidesPerView: 1.2, spaceBetween: 20 },
				640: { slidesPerView: 2.2, spaceBetween: 20 },
				768: { slidesPerView: 3, spaceBetween: 40 },
			};

	if (isSlimContent === null) {
		return <div ref={rootRef} className={styles.Slider} />;
	}

	return (
		<div ref={rootRef} className={styles.Slider}>
			<Swiper
				modules={[Navigation, Pagination, A11y]}
				onSwiper={handleSwiperInit}
				onSlideChange={handleSlideChange}
				spaceBetween={50}
				slidesPerView={2}
				navigation={{
					prevEl: prevRef.current,
					nextEl: nextRef.current,
				}}
				pagination={{
					clickable: true,
					type: 'bullets',
				}}
				breakpoints={breakpoints}
				style={
					{
						'--swiper-pagination-color': 'var(--color-body-text)',
						'--swiper-pagination-bullet-inactive-color': 'transparent',
						'--swiper-pagination-bullet-inactive-opacity': '1',
					} as React.CSSProperties
				}
			>
				{slides.map((slide, index) => (
					<SwiperSlide key={index} className={styles.Slider_slide}>
						<UI_ContentCard {...slide} displayLinkAs="button" />
					</SwiperSlide>
				))}
			</Swiper>
			<button
				ref={prevRef}
				className={classNames(styles.Slider_navButton, styles.Slider_navButton___prev, {
					[styles.Slider_navButton___enabled]: !isBeginning,
					[styles.Slider_navButton___disabled]: isBeginning,
				})}
				aria-label={prevSlideTitle}
				type="button"
				disabled={isBeginning}
			>
				<UI_Icon size="md">
					<ChevronLeft />
				</UI_Icon>
			</button>

			<button
				ref={nextRef}
				className={classNames(styles.Slider_navButton, styles.Slider_navButton___next, {
					[styles.Slider_navButton___enabled]: !isEnd,
					[styles.Slider_navButton___disabled]: isEnd,
				})}
				aria-label={nextSlideTitle}
				type="button"
				disabled={isEnd}
			>
				<UI_Icon size="md">
					<ChevronRight />
				</UI_Icon>
			</button>
		</div>
	);
};
